import { IsoTimestamp, toIsoTimestamp } from './IsoTimestamp';
import { differenceInMinutes, parseISO, subDays } from 'date-fns';

type AdjustedIsoTimeRange = {
  start: IsoTimestamp;
  end: IsoTimestamp;
};

/**
 * Ensures the end timestamp is within 24 hours of the start timestamp by
 * repeatedly subtracting one day from end until the duration is <= 24 hours.
 * Returns the possibly adjusted end timestamp in ISO 8601 format.
 */
export function ensureEndWithin24Hours(startIso: IsoTimestamp, endIso: IsoTimestamp): AdjustedIsoTimeRange {
  const startDate = parseISO(startIso);
  let endDate = parseISO(endIso);

  // If end is more than 24 hours after start, move end back by day(s) until within 24h
  // Using minutes for precision and DST-agnostic comparison
  while (differenceInMinutes(endDate, startDate) > 24 * 60) {
    endDate = subDays(endDate, 1);
  }

  return { start: startIso, end: toIsoTimestamp(endDate) };
}

/**
 * Calculates the availability duration in whole minutes between two ISO timestamps.
 * Returns 0 for negative durations.
 */
export function getAvailabilityMinutes(startIso: string, endIso: string): number {
  const minutes = differenceInMinutes(parseISO(endIso), parseISO(startIso));
  return Math.max(0, minutes);
}
